import type { Ref } from 'react';
import { Box, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import CheckRounded from '@mui/icons-material/CheckRounded';
import CloseRounded from '@mui/icons-material/CloseRounded';
import BlockRounded from '@mui/icons-material/BlockRounded';
import { resolveTone } from './statusSeverity';

/**
 * One node in a {@link WorkflowStepper}. `at` is a **pre-formatted** caption
 * (the caller formats the timestamp with its locale/time zone so this stays
 * copy- and format-agnostic). `tone` marks a branch-outcome step:
 * `error` (Denied / Approval expired) or `neutral` (Cancelled).
 */
export interface WorkflowStep {
  label: string;
  /** Pre-formatted completion caption; shown under completed steps. */
  at?: string | null;
  /** Branch-outcome tint for a terminal step. */
  tone?: 'error' | 'neutral';
}

export interface WorkflowStepperProps {
  steps: WorkflowStep[];
  /** Index of the current (active) step; steps before it render as completed. */
  current: number;
  /** Caption shown under the active step when it has no `at` (translated copy). */
  inProgressLabel?: string;
  ref?: Ref<HTMLDivElement>;
}

/**
 * Vertical lifecycle timeline (the Polaris kit `WorkflowStepper`) — the request
 * lifecycle (Pending approval → Approved → In process → Completed). Completed
 * steps show a filled node + caption; the current step shows a pulse dot; a
 * branch-outcome step (`tone`) replaces the tail with a single tinted step
 * (Denied / Approval expired → error, Cancelled → neutral). Copy/format-agnostic:
 * captions are supplied pre-formatted by the caller.
 */
export function WorkflowStepper({ steps, current, inProgressLabel, ref }: WorkflowStepperProps) {
  const theme = useTheme();
  const accent = theme.palette.secondary.main;
  const success = theme.palette.success.main;
  const error = theme.palette.error.main;
  const neutral = resolveTone(theme, 'neutral').main;

  return (
    <Box ref={ref} sx={{ display: 'flex', flexDirection: 'column' }}>
      {steps.map((step, i) => {
        const done = i < current;
        const active = i === current;
        const failed = step.tone === 'error';
        const cancelled = step.tone === 'neutral';
        const nodeColor = failed
          ? error
          : cancelled
            ? neutral
            : done
              ? success
              : active
                ? accent
                : theme.palette.divider;
        const last = i === steps.length - 1;
        const connectorColor =
          done && !cancelled ? success : cancelled ? neutral : theme.palette.divider;

        return (
          <Box key={i} sx={{ display: 'flex', gap: 1.75 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  flexShrink: 0,
                  display: 'grid',
                  placeItems: 'center',
                  bgcolor: done ? nodeColor : active ? alpha(accent, 0.14) : 'background.paper',
                  border: `2px solid ${nodeColor}`,
                }}
              >
                {done ? (
                  failed ? (
                    <CloseRounded sx={{ fontSize: 13, color: '#fff' }} />
                  ) : cancelled ? (
                    <BlockRounded sx={{ fontSize: 13, color: '#fff' }} />
                  ) : (
                    <CheckRounded sx={{ fontSize: 13, color: '#fff' }} />
                  )
                ) : active ? (
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: accent }} />
                ) : null}
              </Box>
              {!last && (
                <Box sx={{ width: '2px', flex: 1, minHeight: 22, bgcolor: connectorColor }} />
              )}
            </Box>
            <Box sx={{ pb: last ? 0 : '18px', pt: '1px' }}>
              <Typography
                component="div"
                sx={{
                  fontSize: 13,
                  fontWeight: active ? 700 : 600,
                  lineHeight: 1.3,
                  color: done || active ? 'text.primary' : 'text.secondary',
                }}
              >
                {step.label}
              </Typography>
              {step.at ? (
                <Typography
                  component="div"
                  sx={{ fontSize: 11, lineHeight: 1.4, color: 'text.secondary', mt: 0.25 }}
                >
                  {step.at}
                </Typography>
              ) : active && inProgressLabel ? (
                <Typography
                  component="div"
                  sx={{
                    fontSize: 11,
                    fontWeight: 500,
                    lineHeight: 1.4,
                    color: 'secondary.main',
                    mt: 0.25,
                  }}
                >
                  {inProgressLabel}
                </Typography>
              ) : null}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}
